import Badge from '@repo/ui/badge'
import { useState } from 'react'

import {
  excludeBudgetToExternalStaff,
  includeBudgetToExternalStaff
} from '../../../../api/missions/external-staff-mission-actions'
import {
  excludeBudgetToLecStaff,
  includeBudgetToLecStaff
} from '../../../../api/missions/lec-staff-mission-actions'
import {
  assignHotelToStaff,
  assignTicketToStaff,
  excludeBudgetToStaff,
  includeBudgetToStaff,
  unassignHotelToStaff,
  unassignTicketToStaff
} from '../../../../api/missions/staff-mission-actions'
import { MissionTypeEnum } from '../../../../lib/types/enums/mission/mission-type.enum'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'

type Props = {
  id: number
  missionId: number
  text?: string
  condition?: boolean
  type: MissionTypeEnum
  conditionType: 'ticketingHandled' | 'hotelHandled' | 'budgetHandled' | 'missionStaffStatus'
  icon?: (color: string) => React.ReactNode
}

const getBadgeVariant = (value?: boolean) => (value ? 'success' : 'danger')
const getBadgeColor = (value?: boolean) => (value ? 'green' : 'red')

/**
 * Badge that can be clicked to toggle a condition
 * @param icon Icon to display
 * @param text Text to display
 * @param condition Initial condition
 * @param conditionType Type of condition to toggle
 * @param actionParam Parameters to pass to the action
 * @returns Badge that can be clicked to toggle a condition
 *
 */
export const MissionProperty = ({
  id,
  missionId,
  text,
  condition,
  conditionType,
  type,
  icon
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [localCondition, setLocalCondition] = useState(condition)

  /**
   * Handle click on badge
   * this function will call the appropriate action based on the condition type
   * and the previous condition
   * @throws  Error If an error occurs during the action
   * @returns void
   */
  const handleClick = async () => {
    if (loading) return

    setLoading(true)

    const previousCondition = localCondition
    setLocalCondition(!previousCondition)

    try {
      switch (conditionType) {
        case 'ticketingHandled':
          if (previousCondition) await unassignTicketToStaff({ staffId: id, id: missionId })
          else await assignTicketToStaff({ staffId: id, id: missionId })
          break
        case 'hotelHandled':
          if (previousCondition) await unassignHotelToStaff({ staffId: id, id: missionId })
          else await assignHotelToStaff({ staffId: id, id: missionId })
          break
        case 'budgetHandled':
          if (previousCondition) {
            switch (type) {
              case MissionTypeEnum.STAFF:
                await excludeBudgetToStaff({ staffId: id, id: missionId })
                break
              case MissionTypeEnum.EXTERNAL:
                await excludeBudgetToExternalStaff({ id: missionId, externalStaffId: id })
                break
              case MissionTypeEnum.OFFICE:
                await excludeBudgetToLecStaff({ id: missionId, userId: id })
            }
          } else {
            switch (type) {
              case MissionTypeEnum.STAFF:
                await includeBudgetToStaff({ staffId: id, id: missionId })
                break
              case MissionTypeEnum.EXTERNAL:
                await includeBudgetToExternalStaff({ id: missionId, externalStaffId: id })
                break
              case MissionTypeEnum.OFFICE:
                await includeBudgetToLecStaff({ id: missionId, userId: id })
                break
            }
          }
          break
        case 'missionStaffStatus':
          // Do something
          break
      }
    } catch (error) {
      showAxiosError(error as Error) // Handle error
      setLocalCondition(previousCondition) // Revert on error
    }

    setLoading(false)
  }

  return (
    <Badge
      loading={loading}
      text={text}
      icon={icon ? icon(getBadgeColor(localCondition)) : undefined} // Apply dynamic color
      variant={getBadgeVariant(localCondition)}
      onClick={handleClick}
    />
  )
}
